/**
 * 子平八字排盤（無格局／用神論斷）
 *
 * 十神、藏干、大運、五行計數 — 標準 Ziping 曆法數學。
 * 參考：通用子平排盤慣例（非韋千里原文照搬）。
 */
import {
  CalendarInput,
  Gender,
  Pillar,
  TIANGAN,
  DIZHI,
  computeCalendar,
  isYangStem,
  nearestJie,
  jiaziIndex,
  jiaziByIndex,
  ganIndex,
} from './calendar';

export type Wuxing = '木' | '火' | '土' | '金' | '水';

export type ShiShen =
  | '比肩'
  | '劫財'
  | '食神'
  | '傷官'
  | '偏財'
  | '正財'
  | '七殺'
  | '正官'
  | '偏印'
  | '正印'
  | '日主';

export const STEM_WUXING: Record<string, Wuxing> = {
  甲: '木',
  乙: '木',
  丙: '火',
  丁: '火',
  戊: '土',
  己: '土',
  庚: '金',
  辛: '金',
  壬: '水',
  癸: '水',
};

export const BRANCH_WUXING: Record<string, Wuxing> = {
  子: '水',
  丑: '土',
  寅: '木',
  卯: '木',
  辰: '土',
  巳: '火',
  午: '火',
  未: '土',
  申: '金',
  酉: '金',
  戌: '土',
  亥: '水',
};

/** 地支藏干：本氣、中氣、餘氣（標準《三命通會》系） */
export const CANGGAN: Record<string, string[]> = {
  子: ['癸'],
  丑: ['己', '癸', '辛'],
  寅: ['甲', '丙', '戊'],
  卯: ['乙'],
  辰: ['戊', '乙', '癸'],
  巳: ['丙', '庚', '戊'],
  午: ['丁', '己'],
  未: ['己', '丁', '乙'],
  申: ['庚', '壬', '戊'],
  酉: ['辛'],
  戌: ['戊', '辛', '丁'],
  亥: ['壬', '甲'],
};

export const SHENG: Record<Wuxing, Wuxing> = {
  木: '火',
  火: '土',
  土: '金',
  金: '水',
  水: '木',
};

export const KE: Record<Wuxing, Wuxing> = {
  木: '土',
  火: '金',
  土: '水',
  金: '木',
  水: '火',
};

/** 以日干為基準推十神 */
export function shiShenOf(dayGan: string, otherGan: string): ShiShen {
  if (otherGan === dayGan) return '日主';
  const me = STEM_WUXING[dayGan];
  const ot = STEM_WUXING[otherGan];
  if (!me || !ot) return '比肩';
  const samePolarity = isYangStem(dayGan) === isYangStem(otherGan);

  if (ot === me) return samePolarity ? '比肩' : '劫財';
  if (SHENG[me] === ot) return samePolarity ? '食神' : '傷官';
  if (KE[me] === ot) return samePolarity ? '偏財' : '正財';
  if (KE[ot] === me) return samePolarity ? '七殺' : '正官';
  if (SHENG[ot] === me) return samePolarity ? '偏印' : '正印';
  return '比肩';
}

export interface HiddenStemInfo {
  gan: string;
  shiShen: ShiShen;
  wuxing: Wuxing;
}

export interface PillarDetail {
  label: string;
  gan: string;
  zhi: string;
  gz: string;
  ganShiShen: ShiShen;
  ganWuxing: Wuxing;
  zhiWuxing: Wuxing;
  canggan: HiddenStemInfo[];
}

export interface DayunItem {
  /** 1-based 序 */
  index: number;
  gz: string;
  gan: string;
  zhi: string;
  ganShiShen: ShiShen;
  canggan: HiddenStemInfo[];
  /** 起運虛歲（約） */
  startAge: number;
  endAge: number;
  /** 起運西曆年（約，依生日年＋起運歲） */
  startYear: number;
  endYear: number;
}

export interface DayunInfo {
  forward: boolean;
  /** 陽男陰女順／陰男陽女逆 */
  ruleLabel: string;
  /** 起運：年、月（日略） */
  qiYunYears: number;
  qiYunMonths: number;
  qiYunDays: number;
  /** 用於計算的節距天數 */
  jieDays: number;
  jieName: string;
  items: DayunItem[];
}

export interface WuxingCounts {
  /** 四柱天干＋地支（地支按支五行） */
  visible: Record<Wuxing, number>;
  /** 另計藏干 */
  withCanggan: Record<Wuxing, number>;
}

export interface BaziResult {
  gender: Gender;
  calendar: ReturnType<typeof computeCalendar>;
  dayMaster: string;
  pillars: PillarDetail[];
  dayun: DayunInfo;
  wuxing: WuxingCounts;
}

function emptyWx(): Record<Wuxing, number> {
  return { 木: 0, 火: 0, 土: 0, 金: 0, 水: 0 };
}

function pillarDetail(label: string, p: Pillar, dayGan: string, isDay = false): PillarDetail {
  const cang = (CANGGAN[p.zhi] ?? []).map((g) => ({
    gan: g,
    shiShen: shiShenOf(dayGan, g),
    wuxing: STEM_WUXING[g],
  }));
  return {
    label,
    gan: p.gan,
    zhi: p.zhi,
    gz: p.gz,
    ganShiShen: isDay ? '日主' : shiShenOf(dayGan, p.gan),
    ganWuxing: STEM_WUXING[p.gan],
    zhiWuxing: BRANCH_WUXING[p.zhi],
    canggan: cang,
  };
}

/** 大運順逆：陽男陰女順行；陰男陽女逆行（以年干陰陽） */
export function dayunForward(yearGan: string, gender: Gender): boolean {
  const yang = isYangStem(yearGan);
  if (gender === '男') return yang;
  return !yang;
}

function daysToQiYun(days: number): { years: number; months: number; days: number } {
  // 傳統：三日折一年；餘數一日＝四月
  const totalMonths = Math.round(days * 4);
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  const remDays = Math.round((days * 4 - totalMonths) * 30); // 粗略
  return { years, months, days: Math.max(0, remDays) };
}

export function computeDayun(
  monthGz: string,
  dayGan: string,
  yearGan: string,
  gender: Gender,
  birth: Date,
  count = 10,
): DayunInfo {
  const forward = dayunForward(yearGan, gender);
  const ruleLabel = forward ? '陽男陰女順行' : '陰男陽女逆行';
  // 實際：陽年干+男 或 陰年干+女 → 順；否則逆。標籤用經典口訣。
  const label =
    (isYangStem(yearGan) && gender === '男') || (!isYangStem(yearGan) && gender === '女')
      ? '陽男陰女 · 順行'
      : '陰男陽女 · 逆行';

  const near = nearestJie(birth, forward);
  const jieDays = near?.days ?? 0;
  const jieName = near?.jie.name ?? '';
  const qi = daysToQiYun(jieDays);

  const baseIdx = jiaziIndex(monthGz);
  const birthYear = birth.getFullYear();
  // 起運歲（虛歲近似：足歲＋1 常見；此處用「足歲起運年」＝出生年＋qi.years）
  const startAgeBase = qi.years + (qi.months > 0 || qi.days > 0 ? 0 : 0);
  // 顯示用：起運年齡取 years（足歲），若 months>=6 可進位；保持精確 years+months
  const firstAge = qi.years;

  const items: DayunItem[] = [];
  for (let i = 0; i < count; i++) {
    const idx = forward ? baseIdx + 1 + i : baseIdx - 1 - i;
    const gz = jiaziByIndex(idx);
    const gan = gz[0];
    const zhi = gz[1];
    const startAge = firstAge + i * 10;
    const endAge = startAge + 9;
    const startYear = birthYear + startAge;
    const endYear = birthYear + endAge;
    items.push({
      index: i + 1,
      gz,
      gan,
      zhi,
      ganShiShen: shiShenOf(dayGan, gan),
      canggan: (CANGGAN[zhi] ?? []).map((g) => ({
        gan: g,
        shiShen: shiShenOf(dayGan, g),
        wuxing: STEM_WUXING[g],
      })),
      startAge,
      endAge,
      startYear,
      endYear,
    });
  }

  return {
    forward,
    ruleLabel: label,
    qiYunYears: qi.years,
    qiYunMonths: qi.months,
    qiYunDays: qi.days,
    jieDays: Math.round(jieDays * 1000) / 1000,
    jieName,
    items,
  };
}

function countWuxing(pillars: PillarDetail[]): WuxingCounts {
  const visible = emptyWx();
  const withCanggan = emptyWx();
  for (const p of pillars) {
    visible[p.ganWuxing]++;
    visible[p.zhiWuxing]++;
    withCanggan[p.ganWuxing]++;
    for (const c of p.canggan) {
      withCanggan[c.wuxing]++;
    }
  }
  return { visible, withCanggan };
}

export interface BaziInput extends CalendarInput {
  gender: Gender;
}

export function computeBazi(input: BaziInput): BaziResult {
  const calendar = computeCalendar(input);
  const dayGan = calendar.pillars.day.gan;
  const pillars: PillarDetail[] = [
    pillarDetail('年柱', calendar.pillars.year, dayGan),
    pillarDetail('月柱', calendar.pillars.month, dayGan),
    pillarDetail('日柱', calendar.pillars.day, dayGan, true),
    pillarDetail('時柱', calendar.pillars.hour, dayGan),
  ];

  const birth = new Date(input.year, input.month - 1, input.day, input.hour, input.minute);
  const dayun = computeDayun(
    calendar.pillars.month.gz,
    dayGan,
    calendar.pillars.year.gan,
    input.gender,
    birth,
  );

  return {
    gender: input.gender,
    calendar,
    dayMaster: dayGan,
    pillars,
    dayun,
    wuxing: countWuxing(pillars),
  };
}

export { ganIndex, TIANGAN, DIZHI };
